import { Button, Checkbox, IconButton, Typography } from "@suraasa/placebo-ui"
import { useMutation } from "@tanstack/react-query"
import api from "api"
import { SuraasaPlusSubscriptionStatus } from "api/resources/suraasaPlus/types"
import clsx from "clsx"
import { format } from "date-fns"
import SuraasaPlusLogo, {
  LogoVariant,
} from "features/SuraasaPlus/components/SuraasaPlusLogo"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { motion } from "framer-motion"
import { Check, CheckCircle, Xmark } from "iconoir-react"
import { useEffect, useState } from "react"
import { mapErrorsV2 } from "utils/helpers"

import lightOverlay from "../assets/light_layer.webp"
import meditationImg from "../assets/sitting_medication.webp"
import darkOverlay from "../assets/top_dark_layer.webp"
import { DialogThemeMode } from "../utils"
import AdaptiveDialogAndSheet from "./AdaptiveDialogAndSheets"

type Props = {
  open: boolean
  handleClose: () => void
  mode?: DialogThemeMode
  subscriptionId: string
  validThroughDate: string
}

enum CancelSteps {
  offers = "offers",
  confirmation = "confirmation",
  reason = "reason",
  success = "success",
}

const offerTexts = [
  {
    primary: "Unlimited AI Tools",
    secondary: (
      <span className="font-normal text-onSurface-400">
        that save you hours every week.
      </span>
    ),
  },
  {
    primary: "Expert Masterclasses",
    secondary: (
      <span className="font-normal text-onSurface-400">
        with certificates to boost your resume.
      </span>
    ),
  },
  {
    primary: "Unlimited Job Applications",
    secondary: (
      <span className="font-normal text-onSurface-400">
        to advance your career.
      </span>
    ),
  },
  {
    primary: "Unlimited Webinars",
    secondary: (
      <span className="font-normal text-onSurface-400">
        for real-time learning and certification
      </span>
    ),
  },
]

const cancelOptions = [
  "I don't use Suraasa any more",
  "Suraasa Plus is out of my price range",
  "I wanted to try Suraasa Plus temporarily",
  "I had a technical issue with Suraasa Plus",
  "I subscribed by accident",
  "I didn't find Suraasa Plus features valuable",
  "Other",
]

const modeStyles = {
  [DialogThemeMode.dark]: {
    body: "bg-[#0E1525]",
    header:
      "bg-[linear-gradient(180deg,#4C1278_0%,#082743_56.82%,#0E1525_100%)]",
    footer: "bg-gradient-to-b from-[#0E1525]/0 via-[#0E1525] to-[#0E1525]",
    border: "border-onSurface-700",
    checkIcon: "text-[#FFDF77]",
    checkCircle: "text-[#6DE0CB]",
    finalStepBg: "bg-[linear-gradient(180deg,#082743_0%,#0E1525_100%)]",
    text: {
      plus: "#FFFDC9",
      primary: "text-surface",
      secondary: "text-onSurface-400",
      tertiary: "text-onSurface-300",
      highlight: "text-onSurface-200",
    },
    buttons: {
      x: "text-surface hover:bg-onSurface-200/20  active:bg-onSurface-300/40 active:text-onSurface-900",
      text: "text-onSurface-200 hover:bg-white/20 active:bg-white/30",
      primary: "bg-surface text-onSurface-900",
      checkbox:
        "[&>span>svg]:text-[#0E1525] border-onSurface-600 hover:border-onSurface-500 hover:bg-[#1E293B] data-[state=checked]:bg-highlight-400 data-[state=checked]:border-highlight-400",
    },
  },
  [DialogThemeMode.light]: {
    body: "bg-surface",
    header: "bg-surface",
    footer: "bg-surface",
    border: "border-onSurface-200",
    checkIcon: "text-[#F0ABF1]",
    checkCircle: "text-success-500",
    finalStepBg: "bg-surface",
    text: {
      plus: "#828377",
      primary: "text-onSurface-900",
      secondary: "text-onSurface-500",
      tertiary: "text-onSurface-700",
      highlight: "text-onSurface-800",
    },
    buttons: {
      x: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      text: "text-onSurface-700 hover:bg-gray-100 active:bg-gray-200",
      primary: "bg-onSurface-900 text-white",
      checkbox:
        "border-onSurface-400 hover:border-onSurface-500 hover:bg-onSurface-100 data-[state=checked]:bg-primary-500 data-[state=checked]:border-primary-500",
    },
  },
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
}

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } },
}

const OffersStep = ({ styles }: { styles: any }) => (
  <>
    <Typography variant="body" className={clsx("mb-2.5", styles.text.tertiary)}>
      We understand. But before you go, here&apos;s a quick reminder of all the
      benefits you&apos;ll lose:
    </Typography>
    <div className="flex flex-col gap-1.5">
      {offerTexts.map(text => (
        <div key={text.primary} className="flex gap-1">
          <Check className={clsx("size-3", styles.checkIcon)} strokeWidth={2} />
          <Typography variant="strong" className={clsx(styles.text.tertiary)}>
            {text.primary} {text.secondary}
          </Typography>
        </div>
      ))}
    </div>
  </>
)

const ConfirmationStep = () => (
  <div className="flex items-center justify-center">
    <img src={meditationImg} alt="" className="my-3 h-[186px] w-[278px]" />
  </div>
)

const ReasonStep = ({
  styles,
  selectedOption,
  setSelectedOption,
}: {
  styles: any
  selectedOption: number | null
  setSelectedOption: (v: number | null) => void
}) => (
  <>
    {cancelOptions.map((opt, idx) => {
      const isSelected = selectedOption === idx
      return (
        <button
          key={opt}
          onClick={() => setSelectedOption(idx)}
          className={clsx(
            "flex w-full cursor-pointer justify-between border-b py-2 text-start",
            styles.border,
            styles.text.tertiary,
            { "border-none": idx === cancelOptions.length - 1 }
          )}
        >
          <Typography variant="strongSmallBody">{opt}</Typography>
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => setSelectedOption(isSelected ? null : idx)}
            className={clsx("rounded-full", styles.buttons.checkbox)}
          />
        </button>
      )
    })}
  </>
)

const SuccessStep = ({
  styles,
  validThroughDate,
}: {
  styles: any
  validThroughDate: string
}) => (
  <motion.div
    className="mb-4 flex flex-col items-center"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    <motion.div
      initial={{ scale: 0.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 15 }}
    >
      <CheckCircle className={clsx("mb-3 size-7", styles.checkCircle)} />
    </motion.div>

    <motion.div variants={itemVariants}>
      <Typography
        variant="title3"
        className={clsx("mb-1", styles.text.primary)}
      >
        Membership Cancelled
      </Typography>
    </motion.div>

    <motion.div variants={itemVariants}>
      <Typography
        variant="body"
        className={clsx("max-w-[320px] text-center", styles.text.secondary)}
      >
        Your subscription has been cancelled. You can still access your Suraasa
        Plus benefits until{" "}
        <span className={styles.text.highlight}>
          {format(new Date(validThroughDate), "d MMMM yyyy")}
        </span>
        .
      </Typography>
    </motion.div>
  </motion.div>
)

const CancelSuraasaPlus = ({
  open,
  handleClose,
  mode = DialogThemeMode.dark,
  subscriptionId,
  validThroughDate,
}: Props) => {
  const [step, setStep] = useState<CancelSteps>(CancelSteps.offers)
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [isPolling, setIsPolling] = useState(false)

  const { subscriptionDetailsQuery } = useSuraasaPlusContext()
  const styles = modeStyles[mode]

  const headerBg =
    step === CancelSteps.success ? styles.finalStepBg : styles.header
  const overlayImg = mode === DialogThemeMode.dark ? darkOverlay : lightOverlay

  const cancellationReason = cancelOptions[selectedOption || 0]

  function getSheetHeight() {
    switch (step) {
      case CancelSteps.offers:
        return 86
      case CancelSteps.confirmation:
        return 84
      case CancelSteps.reason:
        return 92
      case CancelSteps.success:
        return 47
      default:
        return 47
    }
  }

  const cancelSubscription = useMutation({
    mutationFn: () =>
      api.suraasaPlus.cancelSubscription({
        urlParams: { userId: subscriptionId },
        data: { cancellationReason },
      }),
    onSuccess: () => {
      setIsPolling(true)
    },
    onError: e => mapErrorsV2(e),
  })

  const handleClick = () => {
    if (step === CancelSteps.reason) cancelSubscription.mutate()
    else handleClose()
  }

  useEffect(() => {
    if (!open) {
      setStep(CancelSteps.offers)
      setSelectedOption(null)
    }
  }, [open])

  // The subscription status update is asynchronous and may take some time.
  // Polling is used here to repeatedly check until the status becomes "cancelled".
  useEffect(() => {
    if (!isPolling) return

    let attempts = 0
    const MAX_ATTEMPTS = 10
    const INTERVAL = 1000

    const interval = setInterval(async () => {
      attempts++

      try {
        const res = await subscriptionDetailsQuery?.refetch()
        const status = res?.data?.subscription?.status

        if (status === SuraasaPlusSubscriptionStatus.cancelled) {
          clearInterval(interval)
          setIsPolling(false)
          setStep(CancelSteps.success)
          return
        }
      } catch (err) {
        console.error(err)
        if (attempts >= MAX_ATTEMPTS) {
          mapErrorsV2(err)
        }
      }

      if (attempts >= MAX_ATTEMPTS) {
        clearInterval(interval)
        setIsPolling(false)
        setStep(CancelSteps.success)
      }
    }, INTERVAL)

    return () => clearInterval(interval)
  }, [isPolling, subscriptionDetailsQuery])

  return (
    <AdaptiveDialogAndSheet
      sheetHeight={getSheetHeight()}
      open={open}
      handleClose={handleClose}
      contentClassName="sm:!w-[460px] overflow-hidden rounded-xl"
      bodyClassName={clsx(styles.body, "p-0")}
      body={
        <>
          {/* Header */}
          <div
            className={clsx("relative px-2 py-4 sm:pl-4 sm:pr-3", headerBg, {
              "!p-1": step === CancelSteps.success,
            })}
          >
            {step !== CancelSteps.success && (
              <img
                src={overlayImg}
                alt=""
                className="pointer-events-none absolute -top-px left-0 z-0"
              />
            )}

            <div
              className={clsx("relative z-2 mb-2 flex justify-between", {
                relative: step === CancelSteps.success,
              })}
            >
              {step !== CancelSteps.success && (
                <SuraasaPlusLogo
                  variant={LogoVariant.plus}
                  height={20}
                  width={44}
                  color={styles.text.plus}
                />
              )}
              <IconButton
                className={clsx("rounded-lg", styles.buttons.x, {
                  "absolute top-1 right-1 sm:right-2":
                    step === CancelSteps.success,
                })}
                onClick={handleClose}
              >
                <Xmark className="!size-2.5" strokeWidth={2} />
              </IconButton>
            </div>

            {step === CancelSteps.offers && (
              <Typography
                variant="title2"
                className={clsx("max-w-[315px] leading-7", styles.text.primary)}
              >
                Thinking About Cancelling Suraasa Plus?
              </Typography>
            )}

            {step === CancelSteps.confirmation && (
              <div className="flex flex-col gap-1">
                <Typography
                  className={clsx("text-title2Mobile", styles.text.primary)}
                >
                  Are you sure you want to cancel?
                </Typography>
                <Typography
                  variant="largeBody"
                  className={clsx("max-w-[350px]", styles.text.secondary)}
                >
                  You&apos;ll lose access to all Plus benefits and exclusive
                  features.
                </Typography>
              </div>
            )}

            {step === CancelSteps.reason && (
              <Typography
                variant="title2"
                className={clsx("max-w-[315px] leading-7", styles.text.primary)}
              >
                Why are you cancelling Suraasa Plus?
              </Typography>
            )}
          </div>

          {/* Body */}
          <div className="px-2 sm:px-4">
            {step === CancelSteps.offers && <OffersStep styles={styles} />}

            {step === CancelSteps.confirmation && <ConfirmationStep />}

            {step === CancelSteps.reason && (
              <ReasonStep
                styles={styles}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
              />
            )}

            {step === CancelSteps.success && (
              <SuccessStep
                styles={styles}
                validThroughDate={validThroughDate}
              />
            )}
          </div>

          {/* Footer */}
          <div
            className={clsx(
              "flex w-full flex-col items-center justify-end gap-2 border-0 p-2 sm:flex-row sm:px-4 sm:py-3",
              { "justify-center": step === CancelSteps.success }
            )}
          >
            {step !== CancelSteps.reason && step !== CancelSteps.success && (
              <Button
                variant="text"
                className={clsx("w-full sm:w-fit", styles.buttons.text)}
                onClick={() => {
                  if (step === CancelSteps.offers)
                    setStep(CancelSteps.confirmation)
                  else if (step === CancelSteps.confirmation)
                    setStep(CancelSteps.reason)
                }}
              >
                <Typography variant="strong" className="p-1">
                  Proceed to Cancel
                </Typography>
              </Button>
            )}

            <Button
              className={clsx(
                "w-full rounded-xl px-2 py-1.5 font-semibold sm:w-fit",
                styles.buttons.primary
              )}
              onClick={handleClick}
              disabled={step === CancelSteps.reason && selectedOption === null}
              loading={cancelSubscription.isLoading || isPolling}
            >
              <Typography variant="strong" className="shrink-0">
                {step === CancelSteps.reason
                  ? "Continue"
                  : step === CancelSteps.success
                  ? "Done"
                  : "Keep Suraasa Plus"}
              </Typography>
            </Button>
          </div>
        </>
      }
    />
  )
}

export default CancelSuraasaPlus
